import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { createModuleLogger } from '../../../utils/logger';
import { generateJidVariants, isValidJid } from '../../../utils/phoneUtils';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppContext } from '../../../contexts/AppContext';
import { shouldQueryOrphanConversation } from '../lib/conversationScope';
import { fetchOrphanConversationsByRemoteJid } from './chatSync.helpers';

const log = createModuleLogger('[ChatMetadata]');

export function useChatMetadata(activeConversationId: string | null, connectionId?: string | null) {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { currentPage } = useAppContext();
    const isChatActive = currentPage === 'chat';
    const [conversationDbId, setConversationDbId] = useState<string | null>(null);
    const [remoteJid, setRemoteJid] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
    const scopeKey = `${currentOrganization?.id}:${connectionId}:${activeConversationId}`;
    const activeConversationIdRef = useRef(scopeKey);
    activeConversationIdRef.current = scopeKey;
    const previousConversationIdRef = useRef(scopeKey);
    const isStateStale = previousConversationIdRef.current !== scopeKey;
    const effectiveIsLoading = isLoadingMetadata;
    const effectiveDbId = isStateStale ? null : conversationDbId;
    const effectiveRemoteJid = isStateStale ? null : remoteJid;


    useEffect(() => {
        // Se mudou de conversa, limpa IMEDIATAMENTE os states pro próximo ciclo
        if (previousConversationIdRef.current !== scopeKey) {
            previousConversationIdRef.current = scopeKey;
            setConversationDbId(null);
            setRemoteJid(null);
            setIsLoadingMetadata(true);
        }

        if (!isChatActive || !activeConversationId) {
            setConversationDbId(null);
            setRemoteJid(null);
            setIsLoadingMetadata(false);
            return;
        }
        if (!isValidJid(activeConversationId)) {
            log.warn(`ID de conversa inválido: "${activeConversationId}"`);
            setIsLoadingMetadata(false);
            return;
        }

        if (!user) {
            setIsLoadingMetadata(false);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        const loadMetadata = async () => {
            const convId = activeConversationId;
            try {
                if (cancelled || scopeKey !== activeConversationIdRef.current) return;

                setUserId(user.id);

                const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(convId);

                let query = supabase
                    .from('chat_conversations')
                    .select('id, remote_jid');

                if (isUUID) {
                    query = query.eq('id', convId);
                } else {
                    query = query.in('remote_jid', generateJidVariants(convId));
                }

                if (currentOrganization?.id) {
                    query = query.eq('organization_id', currentOrganization.id);
                }
                if (connectionId) {
                    query = query.eq('connection_id', connectionId);
                }

                let { data: conv } = await query
                    .abortSignal(controller.signal)
                    .order('updated_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                // Lista pode mostrar órfã (connection_id null) — abre o histórico mesmo assim.
                if (
                    !isUUID
                    && shouldQueryOrphanConversation(Boolean(conv), connectionId, currentOrganization?.id)
                ) {
                    const orphan = await fetchOrphanConversationsByRemoteJid(
                        convId,
                        currentOrganization!.id,
                        {
                            select: 'id, remote_jid',
                            orderBy: 'updated_at',
                            limit: 1,
                            signal: controller.signal,
                        },
                    );
                    const row = orphan.data?.[0];
                    conv = row?.id ? { id: row.id, remote_jid: row.remote_jid ?? convId } : null;
                }

                if (cancelled || scopeKey !== activeConversationIdRef.current) return;

                if (conv) {
                    setConversationDbId(conv.id);
                    setRemoteJid(conv.remote_jid);
                } else {
                    setRemoteJid(null);
                }
            } catch (error) {
                if (!cancelled) log.error('Erro ao carregar metadados da conversa:', error);
            } finally {
                if (!cancelled && scopeKey === activeConversationIdRef.current) {
                    setIsLoadingMetadata(false);
                }
            }
        };

        void loadMetadata();
        return () => { cancelled = true; controller.abort(); };
    }, [isChatActive, activeConversationId, currentOrganization?.id, connectionId, user?.id, scopeKey]);

    return {
        conversationDbId: effectiveDbId,
        remoteJid: effectiveRemoteJid,
        setConversationDbId,
        userId,
        isLoadingMetadata: effectiveIsLoading
    };
}
